import { renderSurfaceKind, isCompiledSurfaceKind }
  from "%/dynamic-component-editor/helpers/context-error-report";
import { SmartFeedEntityDynamicElementMixin } from "%/dynamic-component-editor/mixins/smart-feed-entity-dynamic-element-mixin";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import StoryCardClickBehavior from "%/dynamic-component-editor/options/story-card-click-behavior";
import { readBoundContextName, resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";

const EMPTY_STORY_PAGE_ENTITY = Object.freeze({
  storyId: 0,
  orgId: 0,
  authoringUserId: 0,
  authoringName: "",
  authorName: "",
  authorAvatarUrl: "",
  storyTitle: "",
  title: "",
  thumbnail: "",
  text: "",
  textOnly: "",
  hashtags: [],
  segmentOrder: [],
  segments: {},
  imageUrls: [],
  timestampCreated: 0,
  timestampPublished: 0,
  timestampUpdated: 0,
  estimatedSeconds: 0,
  numLikes: 0,
  voteScore: 0,
  voteStatus: 0,
  upvoteScore: 0,
  downvoteScore: 0,
  shareCount: 0,
  commentCount: 0,
  likeStatus: false,
  status: "",
  publishingPlatformName: "",
  isFolder: false,
  folderPosition: 0,
  folderItemCount: 0,
  publishedFolderItemCount: 0,
  childNodes: 0,
  notStartedChildNodes: 0,
  startedChildNodes: 0,
  completedChildNodes: 0,
  leafNodesInTree: 0,
  notStartedLeafNodesInTree: 0,
  startedLeafNodesInTree: 0,
  completedLeafNodesInTree: 0,
  completedChildNodesPercent: 0,
  completedLeafNodesPercent: 0,
  myFolder: 0,
});

export const StoryPageElementMixin = {
  extends: SmartFeedEntityDynamicElementMixin,
  inject: {
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
    useExampleData: { default: () => () => false },
  },
  data() {
    return {
      resolvedUrlStoryId: 0,
    };
  },
  created() {
    if (this.editMode || this.isPreviewMode) {
      return;
    }
    if (!this.entityId && (!this.boundContextName || this.boundContextName === "story")
      && typeof window !== "undefined") {
      const injectedStoryId = window.blclient?.data?.readerStoryId;
      if (injectedStoryId) {
        this.resolvedUrlStoryId = Number(injectedStoryId);
      } else if (isCompiledSurfaceKind(renderSurfaceKind())) {
        const match = window.location.pathname.match(/\/[^/]+\/(\d+)\/?$/);
        if (match) {
          this.resolvedUrlStoryId = Number(match[1]);
        }
      }
    }
    const id = this.storyEntityId;
    if (!id) {
      return;
    }
    useStoryStore().getOrFetchStory(id);
  },
  entityStore: () => useStoryStore(),
  elementPanelSettings: {
    elementGroup: "storyElements",
    elementType: "element",
  },
  computed: {
    countPending() {
      if (this.editMode || this.isPreviewMode || !this.storyEntityId) {
        return false;
      }
      const cached = useStoryStore().stories?.[this.storyEntityId];
      return !(cached?.storyId || cached?.story_id);
    },
    isPreviewMode() {
      const fn = this.useExampleData;
      return typeof fn === "function" ? Boolean(fn()) : Boolean(fn);
    },
    boundContextName() {
      return readBoundContextName(this);
    },
    storyEntityId() {
      return resolveBoundEntityId(this, "story", () => this.resolvedUrlStoryId);
    },
    storyHref() {
      return StoryCardClickBehavior.storyHref(
        this.links.web,
        {
          authorProfile: this.entity.authoringUserId,
          postPage: this.entity.storyId,
          nestedPath: this.entity.nestedUrl,
        },
        this.getOptionValue(StoryCardClickBehavior),
      );
    },
  },
  methods: {
    getExampleEntity() {
      return {
        "storyId": 0,
        "orgId": 0,
        "authoringUserId": 0,
        "title": "The Power of Personal Style: Why Fashion Is More Than Just Clothes",
        "variantParentId": 0,
        "variantType": "",
        "publishingPlatformType": "publication",
        "publishingPlatformId": 0,
        "remixParentId": 0,
        "currentStoryVersionId": 0,
        "isPrivate": 0,
        "status": "active",
        "timestampCreated": 1750778028,
        "timestampUpdated": 1750778040,
        "timestampContentUpdated": 1750778028,
        "timestampPublished": 1750778040,
        "numLikes": 0,
        "numReports": 0,
        "popularityScore": 0,
        "voteScore": 0,
        "moderationScore": 0,
        "moderationStatus": "",
        "lastModeratorId": 0,
        "timestampLastComment": 0,
        "timestampEdited": 0,
        "postCommentCount": 0,
        "isExclusive": 0,
        "allowRemix": 0,
        "requiresSyndicationApproval": 0,
        "originalSyndicatingOrgId": 0,
        "segmentOrder": [{ "storySegmentId": 1, "storySegmentVersionId": 1 }],
        "hashtags": [{ "hashtagId": 1, "hashtag": "inspiration", "status": "active", "timestampCreated": 1751380124, "timestampUpdated": 1751380124 }],
        "variantCount": 1,
        "likeStatus": false,
        "voteStatus": 0,
        "segments": {
          1: {
            "storySegmentVersionId": 1,
            "storySegmentId": 1,
            "segmentContent": JSON.stringify({
              style: "paragraph",
              data: {
                body_text: "Fashion is often seen as superficial—just fabric, trends, and runways. But at its core, fashion is self-expression. It's a language we all speak, even if we're not aware of it. Every choice—from the sneakers you slip on in the morning to the vintage jacket you dig out for special nights—tells a story about who you are, where you've been, and how you want to be seen....",
                text_align: "left",
                ranges: [],
              },
            }),
            "timestampCreated": 1750778037,
            "timestampUpdated": 1750778037,
            "segmentType": "rich_text",
          },
          2: {
            "story_segment_version_id": 2,
            "story_segment_id": 2,
            "segment_content": JSON.stringify({
              src: "https://inspirationdev.blob.core.windows.net/inspiration-post-images/post-image-14-0-6865455c7e4a3",
              width: "50%",
              alignment: "left",
              editorId: "5qb5cdb6ije",
              style: null,
            }),
            "timestamp_created": 1750778037,
            "timestamp_updated": 1750778037,
            "segment_type": "image",
          },
        },
        "thumbnail": "https://inspirationprod.blob.core.windows.net/inspiration-cdn/images/fashion-story-sample-image.webp",
        "text": "Fashion is often seen as superficial—just fabric, trends, and runways. But at its core, fashion is self-expression. It's a language we all speak, even if we're not aware of it. Every choice—from the sneakers you slip on in the morning to the vintage jacket you dig out for special nights—tells a story about who you are, where you've been, and how you want to be seen....",
        "textOnly": "Fashion is often seen as superficial—just fabric, trends, and runways. But at its core, fashion is self-expression. It's a language we all speak, even if we're not aware of it. Every choice—from the sneakers you slip on in the morning to the vintage jacket you dig out for special nights—tells a story about who you are, where you've been, and how you want to be seen....",
        "imageUrls": [
          "https://inspirationprod.blob.core.windows.net/inspiration-cdn/images/fashion-story-sample-image.webp",
        ],
        "upvoteScore": 0,
        "downvoteScore": 0,
        "shareCount": 0,
        "conversationId": 0,
        "authoringName": "John Smith",
        "authoringAvatarUrl": "https://inspirationprod.blob.core.windows.net/inspiration-cdn/images/john-smith-profile-picture.webp",
        "publishingPlatformName": "My Publication",
        "commentCount": 12,
        "estimatedSeconds": 480,
        "isFolder": true,
        "folderPosition": 2,
        "folderItemCount": 5,
        "publishedFolderItemCount": 3,
        "childNodes": 5,
        "notStartedChildNodes": 2,
        "startedChildNodes": 1,
        "completedChildNodes": 2,
        "leafNodesInTree": 10,
        "notStartedLeafNodesInTree": 4,
        "startedLeafNodesInTree": 2,
        "completedLeafNodesInTree": 4,
        "completedChildNodesPercent": 40,
        "completedLeafNodesPercent": 40,
        "myFolder": 0,
        "metadata": [
          { key: "genre", value: "Drama" },
          { key: "release_date", value: "2024-03-01" },
          { key: "synopsis", value: "A gripping tale of triumph." },
          { key: "servings", value: "4" },
          { key: "servings_descriptor", value: "servings" },
          { key: "prep_minutes", value: "15" },
          { key: "prep_minutes_descriptor", value: "minutes" },
          { key: "ingredients", value: "Flour, sugar, eggs, butter" },
          { key: "sets", value: "3" },
          { key: "sets_descriptor", value: "sets" },
          { key: "reps", value: "12" },
          { key: "reps_descriptor", value: "per side" },
          { key: "weight", value: "100" },
          { key: "weight_descriptor", value: "kg" },
          { key: "duration", value: "30" },
          { key: "duration_descriptor", value: "seconds" },
          { key: "rest", value: "60" },
          { key: "rest_descriptor", value: "seconds" },
          { key: "tempo", value: "3-0-1" },
          { key: "season_number", value: "1" },
          { key: "episode_count", value: "10" },
          { key: "episode_count_descriptor", value: "episodes" },
          { key: "episode_number", value: "1" },
          { key: "duration_minutes", value: "42" },
          { key: "duration_minutes_descriptor", value: "minutes" },
          { key: "air_date", value: "2024-03-01" },
        ],
      };
    },
    getRealEntity() {
      if (this.isPreviewMode) {
        return this.getExampleEntity();
      }
      const id = this.storyEntityId;
      if (!id) {
        return EMPTY_STORY_PAGE_ENTITY;
      }
      const cached = useStoryStore().stories?.[id];
      if (cached?.storyId || cached?.story_id) {
        return cached;
      }
      return EMPTY_STORY_PAGE_ENTITY;
    },
  },
};
